import { CircularProgress, styled } from "@mui/material";
import { ErrorBoundary } from "@appsignal/react";
import React, { useMemo } from "react";

import { LegacyWidgetConfiguration, Widget as WidgetType } from "@/lib/api";
import { WidgetTypes } from "@/types/widget";
import { isWidgetErrorResponse, useWidget } from "@/features/dashboard/features/widgets";
import { HTMLTextContainer } from "@/features/shared/widgets/components";
import { appSignal } from "@/appsignal";

import { LEGACY_WIDGETS, WIDGETS, WidgetComponent } from "./widget-registry";

type Props = {
  widget: WidgetType;
};

const FallbackComponent = ({ error }: { error: Error }) => (
  <div>Failed to load widget: {error?.message}</div>
);

const StyledLoadingContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
});

const Widget = ({ widget }: Props) => {
  const { configuration } = widget;

  const { data, status, fetchStatus } = useWidget(widget);

  const widgetOptions = useMemo(
    () => ({
      id: widget.id,
      title: widget.title,
      data: data?.data,
      configuration,
    }),
    [widget, configuration, data],
  );

  if (fetchStatus === "fetching" && status !== "success") {
    return (
      <StyledLoadingContainer>
        <CircularProgress size={20} color="inherit" />
      </StyledLoadingContainer>
    );
  }

  return (
    <ErrorBoundary
      instance={appSignal}
      tags={{ component: "Widget", title: widget.title, widget: widget.id }}
      fallback={({ error }: { error: Error }) => <FallbackComponent error={error} />}
    >
      <WidgetRenderer widget={widget} widgetProps={widgetOptions} />
    </ErrorBoundary>
  );
};

export default Widget;

function isLegacyWidget(
  configuration: WidgetType["configuration"],
): configuration is LegacyWidgetConfiguration {
  return configuration?.type === WidgetTypes.LEGACY;
}

type WidgetRendererViewProps = {
  widget: WidgetType;
  widgetProps: Record<string, unknown>;
};

function WidgetRenderer({ widget, widgetProps }: WidgetRendererViewProps) {
  const isLegacy = isLegacyWidget(widget.configuration);
  const widgetMap = isLegacy ? LEGACY_WIDGETS : WIDGETS;
  const renderer = isLegacy
    ? (widget.configuration as LegacyWidgetConfiguration)?.renderer
    : widget.configuration?.type;

  if (!renderer || !(renderer in widgetMap)) {
    return <div>No element</div>;
  }

  if (isLegacy && isWidgetErrorResponse(widgetProps?.data)) {
    return <HTMLTextContainer key={widget.id} content={widgetProps.data.message} />;
  }

  const Resolved = widgetMap[renderer] as WidgetComponent;

  return React.createElement(Resolved, { key: widget.id, ...widget, ...widgetProps });
}
